// Company time (Asia/Dubai) for everything a person types or reads as a pickup
// time. The API speaks UTC instants (ISO strings); the drivers, the owner and
// the Telegram cards all run on Dubai wall-clock time — so the browser's own
// time zone must never leak into what is entered or shown, or a pickup
// entered as "09:00" by someone abroad would reach the driver as another time.
//
// Everything here goes through Intl with an explicit `timeZone`, never the
// `Date` local getters, so it behaves the same wherever the browser is.

import i18n from "@/i18n";

export const COMPANY_TIMEZONE = "Asia/Dubai";

/** Parse an API timestamp into a Date. A value with no zone ("2026-10-01T05:00:00"
 * — what a SQLite-backed dev / test server hands back) is UTC, exactly how the
 * backend reads it; the browser must never take it for its own local time. */
export function parseInstant(iso: string): Date {
  const zoneless = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(iso);
  return new Date(zoneless ? `${iso}Z` : iso);
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function wallFormatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(tz, f);
  }
  return f;
}

interface Wall {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
}

function wallParts(ms: number, tz: string): Wall {
  const out: Record<string, number> = {};
  for (const p of wallFormatter(tz).formatToParts(new Date(ms))) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  return { y: out.year, mo: out.month, d: out.day, h: out.hour, mi: out.minute, s: out.second };
}

/** How far `tz` is ahead of UTC at this instant, in ms. */
function offsetMs(instantMs: number, tz: string): number {
  const w = wallParts(instantMs, tz);
  return Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s) - Math.floor(instantMs / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-10-01T05:00:00Z" -> "2026-10-01T09:00": the wall clock in `tz`, in the
 * shape the date-time field works in. "" for a missing / invalid value. */
export function isoToZonedWall(
  iso: string | null | undefined,
  tz: string = COMPANY_TIMEZONE,
): string {
  if (!iso) return "";
  const ms = parseInstant(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const w = wallParts(ms, tz);
  return `${w.y}-${pad(w.mo)}-${pad(w.d)}T${pad(w.h)}:${pad(w.mi)}`;
}

/** "2026-10-01T09:00" typed as `tz` wall-clock -> the UTC ISO instant the API
 * stores ("2026-10-01T05:00:00.000Z"). null if the text is not a date-time. */
export function zonedWallToIso(
  wall: string,
  tz: string = COMPANY_TIMEZONE,
): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wall);
  if (!m) return null;
  const asUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  // Two passes settle the offset even next to a DST change (Dubai has none,
  // but `tz` is a parameter).
  let ms = asUtc - offsetMs(asUtc, tz);
  ms = asUtc - offsetMs(ms, tz);
  const out = new Date(ms);
  return Number.isNaN(out.getTime()) ? null : out.toISOString();
}

/** "HH:mm" of an instant on the `tz` clock ("" if missing / invalid). */
export function formatZonedTime(
  iso: string | null | undefined,
  tz: string = COMPANY_TIMEZONE,
): string {
  const wall = isoToZonedWall(iso, tz);
  return wall ? wall.slice(11) : "";
}

/** A locale-formatted date + time of an instant on the company (Dubai) clock,
 * to the minute — "19.09.2026, 22:40". */
export function formatCompanyDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = parseInstant(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(i18n.language, {
    timeZone: COMPANY_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** The Dubai calendar date of an instant, locale-formatted — "19.09.2026". */
export function formatCompanyDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = parseInstant(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(i18n.language, {
    timeZone: COMPANY_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** A plain "YYYY-MM-DD" (a service date, a check-in) in the same "19.09.2026"
 * shape. No time zone is involved — it is a calendar date, not an instant. */
export function formatDateOnly(day: string | null | undefined): string {
  const m = day ? /^(\d{4})-(\d{2})-(\d{2})/.exec(day) : null;
  if (!m) return day ?? "";
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString(i18n.language, {
    timeZone: "UTC",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Today's date in Dubai as "YYYY-MM-DD". */
export function companyToday(): string {
  return isoToZonedWall(new Date().toISOString()).slice(0, 10);
}

/** The hour (0-23) it is in Dubai at `now` — the home page greeting. */
export function companyHour(now: Date = new Date()): number {
  const hour = new Intl.DateTimeFormat("en-GB", {
    timeZone: COMPANY_TIMEZONE,
    hour: "numeric",
    hourCycle: "h23",
  }).format(now);
  return Number(hour) % 24;
}

/** "Четверг, 1 октября" / "Thursday, 1 October" — the Dubai date of `now`. */
export function formatCompanyWeekday(now: Date = new Date(), locale = "en"): string {
  const lang = locale === "ru" ? "ru-RU" : "en-GB";
  const weekday = new Intl.DateTimeFormat(lang, {
    timeZone: COMPANY_TIMEZONE,
    weekday: "long",
  }).format(now);
  const day = new Intl.DateTimeFormat(lang, {
    timeZone: COMPANY_TIMEZONE,
    day: "numeric",
    month: "long",
  }).format(now);
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day}`;
}

/** Today in Dubai as a local-midnight Date, for the date pickers (their grids
 * are built from local Date fields, so "today" must be the Dubai day, whatever
 * the browser's own zone says). */
export function companyTodayDate(): Date {
  const [y, m, d] = companyToday().split("-").map(Number);
  return new Date(y, m - 1, d);
}

// -- FullCalendar ---------------------------------------------------------
//
// FullCalendar can only render "local" (the browser's zone) or "UTC" without an
// extra plugin. The calendar page runs it in "UTC" — one fixed clock, whatever
// the browser is — and feeds it *Dubai wall-clock time written as if it were
// UTC*: a 09:00 Dubai pickup goes in as "…T09:00:00Z", so the grid shows 09:00.
// Anything that comes back out of the calendar (the visible range, "now") is
// converted the other way before it reaches the API.

/** A UTC instant as the calendar's fake-UTC Dubai wall clock. Values that are
 * not a full instant (a plain "2026-10-01" all-day date) pass through. */
export function instantToCalendarClock(iso: string): string {
  if (!/T\d{2}:\d{2}/.test(iso)) return iso; // a plain date has no clock to shift
  const wall = isoToZonedWall(iso);
  return wall ? `${wall}:00Z` : iso;
}

/** A Date the calendar hands back (its UTC fields are the Dubai wall clock) as
 * the real UTC instant it stands for, as an ISO string. */
export function calendarClockToInstant(d: Date): string {
  return zonedWallToIso(d.toISOString().slice(0, 16)) ?? d.toISOString();
}

/** "Now" on the calendar's clock: the Dubai date and time, as fake-UTC. */
export function calendarNow(): Date {
  return new Date(instantToCalendarClock(new Date().toISOString()));
}
